import React, { lazy, type RefObject, Suspense } from 'react';

import Loadable from 'react-loadable';

import { fg } from '@atlaskit/platform-feature-flags';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

import { importConversationAssistant } from './import-conversation-assistant';
import type { ConversationAssistantProps } from './types';

type ConversationAssistantAsyncProps = ConversationAssistantProps & {
	isNav4Enabled?: boolean;
	stagingAreaRef?: RefObject<HTMLDivElement>;
};

const conversationAssistantLazy = lazy(() =>
	importConversationAssistant().then((module) => ({
		default: module.ConversationAssistant,
	})),
);
const createLoadingComponent = () => {
	return UFOLoadHold({
		name: 'LoadingConversationAssistantBundle',
	});
};

const conversationAssistantLoadable = Loadable({
	loader: () => importConversationAssistant().then((m) => m.ConversationAssistant),
	loading: createLoadingComponent,
});

const AsyncConversationAssistant: ((
	props: ConversationAssistantAsyncProps,
) => React.JSX.Element) & {
	preload: () => void;
} = Object.assign(
	(props: ConversationAssistantAsyncProps): React.JSX.Element => {
		if (fg('aimate_conversation_assistant_async_loading')) {
			return React.createElement(
				Suspense,
				{ fallback: createLoadingComponent() },
				React.createElement(conversationAssistantLazy, props),
			);
		}

		if (
			props.useChildrenAsLoadingFallback &&
			props.overridingChildren &&
			fg('ai_agent_sessions_warming_cache')
		) {
			return React.createElement(
				Suspense,
				{ fallback: props.overridingChildren },
				React.createElement(conversationAssistantLazy, props),
			);
		}

		return React.createElement(conversationAssistantLoadable, props);
	},
	{
		preload: (): void => {
			// React.lazy has no preload API, so warm the shared chunk directly on the R19
			// lazy path; keep react-loadable's preload (which also marks it loaded) otherwise.
			if (fg('aimate_conversation_assistant_async_loading')) {
				void importConversationAssistant();
			} else {
				void conversationAssistantLoadable.preload();
			}
		},
	},
);

export default AsyncConversationAssistant;
